import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Send, Loader2, Copy, Check, Settings2, FileText, Upload, Star, Rocket, ChevronDown, ChevronUp, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EngineSelect, EmploymentSelect } from "../components/FlowSelectors";
import { ENGINE_KEYS_LITE } from "../lib/models";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISA_OPTIONS = [
  { value: "", label: "Select visa type..." },
  { value: "H1B", label: "H1B" },
  { value: "OPT", label: "OPT" },
  { value: "OPT STEM", label: "OPT STEM" },
  { value: "CPT", label: "CPT" },
  { value: "Green Card", label: "Green Card" },
  { value: "Green Card EAD", label: "Green Card EAD" },
  { value: "L1", label: "L1" },
  { value: "US Citizen", label: "US Citizen" },
  { value: "TN Visa", label: "TN Visa" },
];

// Shared preferences with OptimizePage — MUST use the same key
const PREFS_KEY = "resumeai_defaults";
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}"); } catch { return {}; }
}
function savePrefs(p) {
  const existing = loadPrefs();
  localStorage.setItem(PREFS_KEY, JSON.stringify({ ...existing, ...p }));
}

export default function WriteEmailPage() {
  const prefs = useRef(loadPrefs());

  const [savedResumes, setSavedResumes] = useState([]);
  const [defaultResumeId, setDefaultResumeId] = useState("");
  const [selectedResumeId, setSelectedResumeId] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeSource, setResumeSource] = useState("default");

  const [jobDescription, setJobDescription] = useState("");
  const [recruiterName, setRecruiterName] = useState("");
  const [recruiterEmail, setRecruiterEmail] = useState("");
  const [emailResult, setEmailResult] = useState("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Settings — synced with Optimize page via localStorage
  const [emailLength, setEmailLength] = useState(prefs.current.emailLength ?? "short");
  const [matchLocation, setMatchLocation] = useState(prefs.current.matchLocation ?? false);
  const [doNotDiscussLocation, setDoNotDiscussLocation] = useState(prefs.current.doNotDiscussLocation ?? false);
  const [includeVisa, setIncludeVisa] = useState(prefs.current.includeVisa ?? false);
  const [visaStatus, setVisaStatus] = useState(prefs.current.visaStatus ?? "");
  const [willingToRelocate, setWillingToRelocate] = useState(prefs.current.willingToRelocate ?? false);
  const [candidateLocation, setCandidateLocation] = useState(prefs.current.candidateLocation ?? "");

  // Auto-detected from JD
  const [detectedEmail, setDetectedEmail] = useState("");
  const [detectedLocation, setDetectedLocation] = useState("");
  const [localsPreferred, setLocalsPreferred] = useState(false);

  // Company details popup
  const [showCompanyPopup, setShowCompanyPopup] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [isThirdParty, setIsThirdParty] = useState(false);
  const [employmentType, setEmploymentType] = useState(prefs.current.employmentType ?? "contract-c2c");
  const [aiModel, setAiModel] = useState(ENGINE_KEYS_LITE.includes(prefs.current.aiModel) ? prefs.current.aiModel : "glm-5.2");

  // All toggles are fully independent — LLM interprets the JD + toggles together
  const handleMatchLocationToggle = () => setMatchLocation(!matchLocation);
  const handleDoNotDiscussToggle = () => setDoNotDiscussLocation(!doNotDiscussLocation);
  const handleRelocateToggle = () => setWillingToRelocate(!willingToRelocate);

  // Sync settings to shared localStorage
  useEffect(() => {
    savePrefs({ emailLength, matchLocation, doNotDiscussLocation, includeVisa, visaStatus, willingToRelocate, candidateLocation, aiModel, employmentType });
  }, [emailLength, matchLocation, doNotDiscussLocation, includeVisa, visaStatus, willingToRelocate, candidateLocation, aiModel, employmentType]);

  // Sync candidateLocation back to profile on blur (not every keystroke)
  const syncLocationToProfile = async (loc) => {
    try { await axios.put(`${API}/auth/profile`, { current_location: loc }); } catch {}
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/resumes`);
        setSavedResumes(res.data || []);
        const def = res.data?.find(r => r.is_default);
        if (def) setDefaultResumeId(def.id);
      } catch { /* silent */ }
      // Fetch location from profile if not already set via localStorage
      try {
        const { data } = await axios.get(`${API}/auth/profile`);
        if (data.current_location && !candidateLocation) {
          setCandidateLocation(data.current_location);
        }
      } catch { /* silent */ }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-analyze JD for recruiter email, location, and local classification (LLM-powered)
  const [classifying, setClassifying] = useState(false);
  useEffect(() => {
    if (!jobDescription || jobDescription.length < 50) return;
    const timeout = setTimeout(async () => {
      setClassifying(true);
      try {
        const res = await axios.post(`${API}/analyze-jd`, { job_description: jobDescription });
        if (res.data.recruiter_email) {
          setDetectedEmail(res.data.recruiter_email);
          if (!recruiterEmail) setRecruiterEmail(res.data.recruiter_email);
        }
        if (res.data.job_location) setDetectedLocation(res.data.job_location);
        setLocalsPreferred(res.data.locals_preferred || false);
      } catch { /* silent */ }
      setClassifying(false);
    }, 1200);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDescription]);

  const handleGenerate = async () => {
    if (!jobDescription.trim()) { toast.error("Please enter a job description"); return; }
    if (includeVisa && !visaStatus) {
      toast.error("Please select your work authorization status");
      setAdvancedOpen(true);
      return;
    }
    // Always show company details popup
    setShowCompanyPopup(true);
  };

  const handleCompanyPopupSubmit = async () => {
    if (!companyName.trim()) {
      toast.error("Company name is required"); return;
    }
    setShowCompanyPopup(false);
    await runEmailGeneration();
  };

  const runEmailGeneration = async () => {
    setGenerating(true); setEmailResult("");
    try {
      const formData = new FormData();
      formData.append("job_description", jobDescription);
      formData.append("recruiter_name", recruiterName);
      formData.append("recruiter_email", recruiterEmail);
      formData.append("company_name", companyName.trim());
      formData.append("is_third_party", isThirdParty ? "true" : "false");
      formData.append("employment_type", employmentType);
      formData.append("ai_model", aiModel);
      formData.append("include_visa", includeVisa && visaStatus ? "true" : "false");
      formData.append("visa_status", visaStatus);
      formData.append("match_location", matchLocation ? "true" : "false");
      formData.append("job_location", detectedLocation);
      formData.append("candidate_location", candidateLocation);
      formData.append("email_length", emailLength);
      formData.append("do_not_discuss_location", doNotDiscussLocation ? "true" : "false");
      formData.append("willing_to_relocate", willingToRelocate ? "true" : "false");

      if (resumeSource === "upload" && resumeFile) {
        formData.append("resume", resumeFile);
      } else if (resumeSource === "saved" && selectedResumeId) {
        formData.append("saved_resume_id", selectedResumeId);
      } else if (defaultResumeId) {
        formData.append("saved_resume_id", defaultResumeId);
      }

      const submitRes = await axios.post(`${API}/write-email`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = submitRes.data.job_id;

      const result = await new Promise((resolve, reject) => {
        const start = Date.now();
        const poll = async () => {
          if (Date.now() - start > 120000) { reject(new Error("Timed out")); return; }
          try {
            const statusRes = await axios.get(`${API}/generate-email/status/${jobId}`);
            if (statusRes.data.status === "completed" && statusRes.data.result) {
              resolve(statusRes.data.result);
            } else if (statusRes.data.status === "failed") {
              reject(new Error(statusRes.data.error || "Generation failed"));
            } else { setTimeout(poll, 2000); }
          } catch { setTimeout(poll, 2000); }
        };
        poll();
      });

      const emailText = typeof result === "string" ? result : (result.email_body || result.email || "");
      setEmailResult(emailText);
      toast.success("Email generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to generate email");
    } finally { setGenerating(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(emailResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard");
  };

  const handleOpenGmail = () => {
    if (!emailResult) return;
    const lines = emailResult.split("\n");
    let subject = "";
    let body = emailResult;
    const subjectLine = lines.find(l => l.toLowerCase().startsWith("subject:"));
    if (subjectLine) {
      subject = subjectLine.replace(/^subject:\s*/i, "").trim();
      body = lines.filter(l => !l.toLowerCase().startsWith("subject:")).join("\n").trim();
    }
    const to = recruiterEmail.trim() || detectedEmail;
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1${to ? `&to=${encodeURIComponent(to)}` : ""}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, "_blank");
  };

  const defaultResume = savedResumes.find(r => r.is_default);

  return (
    <div className="page-container fade-in" data-testid="write-email-page">
      <div className="page-header">
        <div>
          <h1 data-testid="email-page-title">Job Application Email</h1>
          <p className="page-subtitle">Generate professional application emails tailored to the job description.</p>
        </div>
      </div>

      <div className="email-gen-grid">
        {/* Left: Inputs */}
        <div className="card" data-testid="email-input-card">
          <div className="card-header"><h3>Input</h3></div>

          {/* Resume Source */}
          <div className="email-section" data-testid="email-resume-section">
            <label className="email-label">Resume Source</label>
            {defaultResume && (
              <div className="email-default-resume-info" data-testid="email-default-resume-info">
                <Star size={14} /> Using default: <strong>{defaultResume.display_name}</strong>
              </div>
            )}
            <div className="source-toggle" style={{ marginBottom: "0.5rem" }}>
              <button className={`source-toggle-btn ${resumeSource === "default" ? "active" : ""}`}
                onClick={() => setResumeSource("default")} data-testid="email-source-default">
                <Star size={14} /> Default Resume
              </button>
              {savedResumes.length > 1 && (
                <button className={`source-toggle-btn ${resumeSource === "saved" ? "active" : ""}`}
                  onClick={() => setResumeSource("saved")} data-testid="email-source-saved">
                  <FileText size={14} /> Other Resume
                </button>
              )}
              <button className={`source-toggle-btn ${resumeSource === "upload" ? "active" : ""}`}
                onClick={() => setResumeSource("upload")} data-testid="email-source-upload">
                <Upload size={14} /> Upload
              </button>
            </div>
            {resumeSource === "saved" && (
              <select className="email-select" value={selectedResumeId} onChange={e => setSelectedResumeId(e.target.value)} data-testid="email-resume-select">
                <option value="">Select resume...</option>
                {savedResumes.filter(r => !r.is_default).map(r => (
                  <option key={r.id} value={r.id}>{r.display_name}</option>
                ))}
              </select>
            )}
            {resumeSource === "upload" && (
              <input type="file" accept=".doc,.docx" onChange={e => setResumeFile(e.target.files?.[0] || null)} data-testid="email-file-input" className="email-file-input" />
            )}
          </div>

          {/* Recruiter Name & Email */}
          <div className="email-section" style={{ display: "flex", gap: "0.75rem" }}>
            <div style={{ flex: 1 }}>
              <label className="email-label">Recruiter Name <span style={{ opacity: 0.5 }}>(optional)</span></label>
              <input className="email-input" placeholder="e.g. John Smith" value={recruiterName} onChange={e => setRecruiterName(e.target.value)} data-testid="email-recruiter-name" />
            </div>
            <div style={{ flex: 1 }}>
              <label className="email-label">Recruiter Email {detectedEmail && !recruiterEmail && <span className="auto-detected-badge" style={{marginLeft: "0.3rem"}}>Auto-detected</span>}</label>
              <input className="email-input" placeholder={detectedEmail || "e.g. john@company.com"} value={recruiterEmail} onChange={e => setRecruiterEmail(e.target.value)} data-testid="email-recruiter-email" />
            </div>
          </div>

          {/* Job Description */}
          <div className="email-section">
            <label className="email-label">Job Description *</label>
            <textarea className="email-textarea" rows={8} placeholder="Paste the full job description here..." value={jobDescription} onChange={e => setJobDescription(e.target.value)} data-testid="email-jd-input" />
            {(detectedLocation || localsPreferred) && (
              <div style={{ fontSize: "0.7rem", opacity: 0.6, marginTop: "0.3rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                {detectedLocation && <span>Location: {detectedLocation}</span>}
                {localsPreferred && <span className="auto-detected-badge">Locals Preferred</span>}
              </div>
            )}
          </div>

          {/* Email Length Toggle */}
          <div className="email-section">
            <label className="email-label">Email Length</label>
            <div className="source-toggle" data-testid="email-length-toggle">
              <button className={`source-toggle-btn ${emailLength === "short" ? "active" : ""}`}
                onClick={() => setEmailLength("short")} data-testid="email-length-short">
                Short (4-6 lines)
              </button>
              <button className={`source-toggle-btn ${emailLength === "long" ? "active" : ""}`}
                onClick={() => setEmailLength("long")} data-testid="email-length-long">
                Long (10-14 lines)
              </button>
            </div>
          </div>

          {/* AI Engine + Employment Type */}
          <div className="email-section">
            <label className="email-label">AI Engine &amp; Employment Type</label>
            <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
              <EngineSelect value={aiModel} onChange={setAiModel} keys={ENGINE_KEYS_LITE} testid="email-engine-select" className="flex-1" />
              <EmploymentSelect value={employmentType} onChange={setEmploymentType} testid="email-employment-select" className="flex-1" />
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="email-settings" data-testid="email-settings">
            <button className="email-settings-header" onClick={() => setAdvancedOpen(!advancedOpen)}
              style={{ cursor: "pointer", background: "none", border: "none", display: "flex", alignItems: "center", gap: "0.4rem", padding: 0, color: "inherit", font: "inherit", width: "100%" }}
              data-testid="email-advanced-toggle">
              <Settings2 size={14} /> Advanced Settings
              {advancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {advancedOpen && (
              <div style={{ marginTop: "0.75rem" }}>
                {/* Match Job Location */}
                <div className="email-toggle-row">
                  <label className="email-toggle-label">Match Job Location {classifying ? <span className="auto-detected-badge" style={{marginLeft: "0.3rem", opacity: 0.7}}>Analyzing...</span> : localsPreferred ? <span className="auto-detected-badge" style={{marginLeft: "0.3rem"}}>Locals preferred</span> : null}</label>
                  <button className={`toggle-switch ${matchLocation ? "on" : ""}`}
                    onClick={handleMatchLocationToggle}
                    data-testid="email-toggle-location">
                    <span className="toggle-knob" />
                  </button>
                </div>
                {matchLocation && localsPreferred && (
                  <p style={{ fontSize: "0.7rem", opacity: 0.6, margin: "0.25rem 0 0.5rem", lineHeight: 1.3 }}>
                    Locals preferred detected — you will be presented as a local candidate.
                  </p>
                )}
                {matchLocation && !localsPreferred && !classifying && (
                  <p style={{ fontSize: "0.7rem", opacity: 0.6, margin: "0.25rem 0 0.5rem", lineHeight: 1.3 }}>
                    No local restriction detected — you will be presented as willing to relocate.
                  </p>
                )}

                {/* Do Not Discuss Current Location */}
                <div className="email-toggle-row">
                  <label className="email-toggle-label">Do Not Discuss Current Location</label>
                  <button className={`toggle-switch ${doNotDiscussLocation ? "on" : ""}`}
                    onClick={handleDoNotDiscussToggle}
                    data-testid="email-toggle-no-location">
                    <span className="toggle-knob" />
                  </button>
                </div>
                {doNotDiscussLocation && (
                  <p style={{ fontSize: "0.7rem", opacity: 0.6, margin: "0.25rem 0 0.5rem", lineHeight: 1.3 }}>
                    Your current location will not be mentioned. Relocation may still be included.
                  </p>
                )}

                {/* Willing to Relocate */}
                <div className="email-toggle-row">
                  <label className="email-toggle-label">Willing to Relocate</label>
                  <button className={`toggle-switch ${willingToRelocate ? "on" : ""}`}
                    onClick={handleRelocateToggle}
                    data-testid="email-toggle-relocate">
                    <span className="toggle-knob" />
                  </button>
                </div>

                {/* Include Work Authorization */}
                <div className="email-toggle-row">
                  <label className="email-toggle-label">Include Work Authorization</label>
                  <button className={`toggle-switch ${includeVisa ? "on" : ""}`}
                    onClick={() => setIncludeVisa(!includeVisa)}
                    data-testid="email-toggle-visa">
                    <span className="toggle-knob" />
                  </button>
                </div>
                {includeVisa && (
                  <div className="select-wrapper" style={{ marginTop: "0.25rem" }} data-testid="email-visa-select-wrapper">
                    <select className="styled-select" value={visaStatus} onChange={e => { setVisaStatus(e.target.value); savePrefs({ visaStatus: e.target.value }); }} data-testid="email-visa-select">
                      {VISA_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <ChevronDown size={14} className="select-icon" />
                  </div>
                )}

                {/* Current Location — synced with Settings */}
                <div className="email-toggle-row" style={{ marginTop: "0.5rem", flexDirection: "column", alignItems: "flex-start", gap: "0.25rem" }}>
                  <label className="email-toggle-label" style={{ fontSize: "0.75rem" }}>Your Current Location</label>
                  <input className="optional-input" placeholder="e.g. Austin, TX" value={candidateLocation}
                    onChange={e => setCandidateLocation(e.target.value)}
                    onBlur={e => syncLocationToProfile(e.target.value)}
                    data-testid="email-candidate-location"
                    style={{ width: "100%", marginTop: 0 }} />
                  <p style={{ fontSize: "0.65rem", opacity: 0.5, margin: 0, lineHeight: 1.2 }}>
                    Synced with Settings. Used based on your toggle choices above.
                  </p>
                </div>
              </div>
            )}
          </div>

          <button className="btn-secondary-optimize" onClick={handleGenerate} disabled={generating || !jobDescription.trim()} data-testid="email-generate-btn">
            {generating ? <><Loader2 size={16} className="spin" /> Generating...</> : <><Send size={16} /> Generate Email</>}
          </button>
        </div>

        {/* Right: Result */}
        <div className="card" data-testid="email-result-card">
          <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3>Generated Email</h3>
            {emailResult && (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button className="btn-icon" onClick={handleCopy} data-testid="email-copy-btn" title="Copy to clipboard">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
                <button className="btn-icon" onClick={handleOpenGmail} data-testid="email-gmail-btn"
                  title="Open in Gmail"
                  style={{ color: "#d93025", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <Rocket size={16} />
                </button>
              </div>
            )}
          </div>
          {emailResult ? (
            <pre className="email-result-pre" data-testid="email-result">{emailResult}</pre>
          ) : (
            <div className="email-empty-state" data-testid="email-empty">
              <Send size={28} />
              <p>Your application email will appear here.</p>
              <p className="email-empty-sub">Paste a JD and click Generate.</p>
            </div>
          )}
        </div>
      </div>

      {/* Company Details Popup */}
      {showCompanyPopup && (
        <div className="modal-overlay" data-testid="email-company-popup">
          <div className="modal-box" style={{ maxWidth: "480px" }}>
            <div className="modal-header">
              <FileText size={18} style={{ color: "var(--accent)" }} />
              <h3>Company Details</h3>
              <button className="btn-icon" onClick={() => setShowCompanyPopup(false)}><X size={16} /></button>
            </div>
            <p className="modal-desc" style={{ marginBottom: "1rem", opacity: 0.7, fontSize: "0.85rem" }}>
              Enter the company details to generate a tailored application email.
            </p>

            <div style={{ marginBottom: "1rem" }}>
              <label className="email-label" style={{ marginBottom: "0.3rem", display: "block", fontWeight: 600 }}>Company Name *</label>
              <input
                className="email-input"
                placeholder="Enter company name..."
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                autoFocus
                data-testid="email-company-name-input"
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label className="email-label" style={{ marginBottom: "0.3rem", display: "block", fontWeight: 600 }}>Is this a Third-Party / Staffing Company?</label>
              <div className="source-toggle" style={{ marginTop: "0.3rem" }}>
                <button
                  className={`source-toggle-btn ${!isThirdParty ? "active" : ""}`}
                  onClick={() => setIsThirdParty(false)}
                  data-testid="email-third-party-no"
                  style={{ flex: 1 }}
                >No — Direct Hire</button>
                <button
                  className={`source-toggle-btn ${isThirdParty ? "active" : ""}`}
                  onClick={() => setIsThirdParty(true)}
                  data-testid="email-third-party-yes"
                  style={{ flex: 1 }}
                >Yes — Staffing / Agency</button>
              </div>
            </div>

            <div style={{ marginBottom: "1.2rem" }}>
              <label className="email-label" style={{ marginBottom: "0.3rem", display: "block", fontWeight: 600 }}>Employment Type</label>
              <EmploymentSelect value={employmentType} onChange={setEmploymentType} testid="email-emp-select-popup" />
            </div>

            <button className="btn-secondary-optimize" onClick={handleCompanyPopupSubmit} data-testid="email-company-submit" style={{ width: "100%" }}>
              <Sparkles size={16} /> Generate Email
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
